import "./canvas-text-element.css";

import { useQuery } from "@apollo/client";
import { Property } from "csstype";
import React, { ReactElement } from "react";

import { graphql } from "../../generated/gql";
import {
  Maybe,
  TextAlignHorizontal,
  TextAlignVertical,
} from "../../generated/gql/graphql";
import {
  CanvasElementProps,
  CanvasElement,
} from "../layout/editor/canvas-element";

import { CanvasObjectText } from "./canvas-object-text";

export type CanvasTextElementProps = Omit<
  CanvasElementProps,
  "canvasObject" | "canvasObjectSize" | "imageUrl"
> & {
  text: CanvasObjectText;

  objectChanged: (data: Partial<Omit<CanvasObjectText, "kind">>) => void;
};

const RenderTextSvgDocument = graphql(`
  query RenderTextSVG($input: RenderTextSvgInput!) {
    renderTextSvg(input: $input) {
      svg
      dataUrl
      width
      height
    }
  }
`);

export function CanvasTextElement(props: CanvasTextElementProps): ReactElement {
  const { text, ...canvasElementProps } = props;

  // Render the text as an SVG
  const renderTextResult = useQuery(RenderTextSvgDocument, {
    variables: {
      input: {
        fontId: text.text.fontId,
        content: text.text.content,
        alignHorizontal: text.text.alignHorizontal,
      },
    },
  });

  function getJustifyContent(
    alignHorizontal: Maybe<TextAlignHorizontal>,
  ): Property.JustifyContent | undefined {
    switch (alignHorizontal) {
      case TextAlignHorizontal.Left:
        return "flex-start";
      case TextAlignHorizontal.Center:
        return "center";
      case TextAlignHorizontal.Right:
        return "flex-end";
      default:
        return undefined;
    }
  }

  function getAlignItems(
    alignVertical: Maybe<TextAlignVertical>,
  ): Property.AlignItems | undefined {
    switch (alignVertical) {
      case TextAlignVertical.Top:
        return "flex-start";
      case TextAlignVertical.Middle:
        return "center";
      case TextAlignVertical.Bottom:
        return "flex-end";
      default:
        return undefined;
    }
  }

  let imageComponent;
  let imageSize;
  if (renderTextResult.data?.renderTextSvg) {
    imageComponent = (
      <div
        dangerouslySetInnerHTML={{
          __html: renderTextResult.data.renderTextSvg.svg,
        }}
        className="svg-wrapper"
        style={{
          fill: text.text.color ?? undefined,
          display: "flex",
          justifyContent: getJustifyContent(text.text.alignHorizontal),
          alignItems: getAlignItems(text.text.alignVertical),
        }}
      />
    );
    imageSize = {
      width: renderTextResult.data.renderTextSvg.width,
      height: renderTextResult.data.renderTextSvg.height,
    };
  } else {
    // Fallback to a placeholder if rendering fails
    imageSize = {
      width: 100,
      height: 50,
    };
  }

  function handleResize(newSize: { width: number; height: number }): void {
    canvasElementProps.objectChanged({
      text: {
        ...text.text,
        width: newSize.width,
        height: newSize.height,
      },
    });
  }

  return (
    <CanvasElement
      canvasObject={text}
      canvasObjectSize={{
        width: text.text.width ?? imageSize.width,
        height: text.text.height ?? imageSize.height,
      }}
      lockAspectRatio={false}
      ignoreScale={true}
      onResize={handleResize}
      imageComponent={imageComponent}
      {...canvasElementProps}
    />
  );
}
